import { t } from '@lingui/macro'
import { Box, Chip, CircularProgress, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material'
import { PatientManagerIndexDTO } from '@om1/aspen-api/codegen/client/models/PatientManagerIndexDTO'
import DOMPurify from 'dompurify'
import React from 'react'

/**
 * Sanitizes text content to prevent XSS attacks using DOMPurify
 * Removes any HTML tags and malicious scripts
 */
const sanitizeText = (text: string | undefined | null): string => {
    if (!text) return ''
    // Use DOMPurify to sanitize text content, returning plain text only
    return DOMPurify.sanitize(String(text), { ALLOWED_TAGS: [] })
}

export interface PatientManagerFilterProps {
    value?: string | null
    onChange?: (patientManagerId: string | null, patientManager: PatientManagerIndexDTO | null) => void
    label?: string
    showAllOption?: boolean
    showActiveOnly?: boolean
    size?: 'small' | 'medium'
    className?: string
    // Required props - data must be passed from parent
    patientManagers: PatientManagerIndexDTO[]
    loading: boolean
    error: string | null
}

export const PatientManagerFilter: React.FC<PatientManagerFilterProps> = ({
    value,
    onChange,
    label = t`Patient Manager`,
    showAllOption = true,
    showActiveOnly = true,
    size = 'small',
    className,
    patientManagers,
    loading,
    error
}) => {
    // No more API calls - data comes from props only!

    const handleChange = (event: any) => {
        const selectedValue = event.target.value
        onChange?.(selectedValue === '' ? null : selectedValue, patientManagers.find((pm) => pm.id === selectedValue) ?? null)
    }

    const selectedManager = patientManagers.find((pm) => pm.id === value)

    return (
        <FormControl size={size} className={className} sx={{ minWidth: 200 }}>
            <InputLabel>{label}</InputLabel>
            <Select
                value={value || ''}
                onChange={handleChange}
                label={label}
                disabled={loading}
                displayEmpty
                renderValue={(selected) => {
                    if (!selected) {
                        return ''
                    }
                    return selectedManager ? (
                        <Box display='flex' alignItems='center' gap={1}>
                            <Typography variant='body2'>{sanitizeText(selectedManager.name)}</Typography>
                            <Chip
                                label={selectedManager.active ? t`Active` : t`Inactive`}
                                size='small'
                                color={selectedManager.active ? 'success' : 'default'}
                            />
                        </Box>
                    ) : (
                        <Typography color='text.secondary'>{t`Select Patient Manager`}</Typography>
                    )
                }}
                endAdornment={loading ? <CircularProgress size={20} /> : undefined}
            >
                {showAllOption && (
                    <MenuItem value=''>
                        <Typography color='text.secondary'>{t`All Patient Managers`}</Typography>
                    </MenuItem>
                )}
                {patientManagers.map((manager) => (
                    <MenuItem key={manager.id} value={manager.id}>
                        <Box display='flex' flexDirection='column' width='100%'>
                            <Typography variant='body2' fontWeight='medium'>
                                {sanitizeText(manager.name)}
                            </Typography>
                            <Typography variant='caption' color='text.secondary'>
                                {sanitizeText(manager.email)}
                            </Typography>
                        </Box>
                    </MenuItem>
                ))}
                {loading && (
                    <MenuItem disabled>
                        <Box display='flex' alignItems='center' gap={1}>
                            <CircularProgress size={16} />
                            <Typography variant='body2'>{t`Loading...`}</Typography>
                        </Box>
                    </MenuItem>
                )}
                {error && (
                    <MenuItem disabled>
                        <Typography variant='body2' color='error'>
                            {t`Failed to load patient managers`}
                        </Typography>
                    </MenuItem>
                )}
            </Select>
        </FormControl>
    )
}

export default PatientManagerFilter
